import Lozenge from '@atlaskit/lozenge';
import { Box, Flex, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import React from 'react';

import type { Action } from '../../../common/types';

import { DefaultActionButton } from './default-action-button';
import { LinkActionButton } from './link-action-button';

type CardProps = {
	title: string;
	description: string;
	icon?: React.ReactNode;
	primaryAction?: Action;
	secondaryAction?: Action;
	type?: 'suggestion' | 'regular';
	uniformActions?: boolean;
	lozenge?: {
		text: string;
		appearance?: 'default' | 'inprogress' | 'moved' | 'new' | 'removed' | 'success';
		isBold?: boolean;
	};
	testId?: string;
};

const iconContainerStyles = xcss({
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'center',
	width: '32px',
	height: '32px',
});

const contentContainerStyles = xcss({
	flex: 1,
});

const cardSizeStyles = xcss({
	padding: 'space.150',
	borderRadius: 'border.radius.100',
	border: `1px solid ${token('color.border')}`,
	backgroundColor: 'elevation.surface.overlay',
	':hover': {
		backgroundColor: 'elevation.surface.overlay.hovered',
	},
});

const actionsContainerStyles = xcss({
	alignItems: 'flex-start',
	display: 'flex',
	flexDirection: 'column',
	gap: 'space.050',
});

const suggestionActionsContainerStyles = xcss({
	alignItems: 'flex-start',
	display: 'flex',
	flexDirection: 'row',
	gap: 'space.050',
});

const primaryActionStyles = xcss({
	border: `1px solid ${token('color.border')}`,
	borderRadius: 'border.radius.200',
});

export const Card = ({
	title,
	description,
	icon,
	primaryAction,
	secondaryAction,
	type = 'regular',
	uniformActions = false,
	lozenge,
	testId,
}: CardProps) => {
	return (
		<Box xcss={cardSizeStyles} testId={testId ? `${testId}-card` : undefined}>
			<Flex gap="space.100">
				<Box xcss={iconContainerStyles}>{icon}</Box>
				<Box xcss={contentContainerStyles}>
					<Stack space={type === 'suggestion' ? 'space.050' : 'space.100'}>
						<Stack space={type === 'suggestion' ? 'space.050' : 'space.100'}>
							<Flex gap="space.100" alignItems="center">
								<Text
									size="medium"
									weight={type === 'suggestion' ? 'regular' : 'bold'}
									testId={testId ? `${testId}-card-title` : undefined}
								>
									{title}
								</Text>
								{lozenge && (
									<Box>
										<Lozenge appearance={lozenge.appearance} isBold={lozenge.isBold}>
											{lozenge.text}
										</Lozenge>
									</Box>
								)}
							</Flex>

							<Text size="medium" color="color.text.subtlest">
								{description}
							</Text>
						</Stack>
						<Box
							xcss={
								type === 'suggestion' ? suggestionActionsContainerStyles : actionsContainerStyles
							}
						>
							{primaryAction && (
								<Box xcss={primaryActionStyles}>
									{primaryAction.action && <DefaultActionButton act={primaryAction} />}
									{primaryAction && primaryAction.children && primaryAction.children}
									{primaryAction && primaryAction.href && primaryAction.href.link && (
										<LinkActionButton action={primaryAction} />
									)}
								</Box>
							)}

							{secondaryAction && (
								<Box xcss={uniformActions ? primaryActionStyles : undefined}>
									{secondaryAction.action && <DefaultActionButton act={secondaryAction} />}
									{secondaryAction.children && secondaryAction.children}
									{secondaryAction.href && secondaryAction.href.link && (
										<LinkActionButton action={secondaryAction} />
									)}
								</Box>
							)}
						</Box>
					</Stack>
				</Box>
			</Flex>
		</Box>
	);
};
